import {getId, getType} from 'apis/model/base'
import React, {useCallback, useMemo} from 'react'
import {branch, compose, fromRenderProps, nest, withProps} from 'recompose'
import {DelayRender} from 'views/Discovery/DelayRender'
import {SettingsMenu} from 'views/AppLocals/factory/createSettingLayout'
import DefaultTemplate from 'views/AppLocals/Templates/DefaultTemplate'
import {renderOwnChild} from 'views/Shared'
import CustomSiderMenu from 'views/Shared/components/CustomSiderMenu'
import {Queries} from 'views/Shared/enums/Queries'
import {useMenuHandler} from 'views/Shared/handlers/useMenuHandler'
import WorkspaceContext from '../WorkspaceContext'
import CatholicHouseholds from "./containers/CatholicHouseholds";
import PendingCatholicHouseholds from "./containers/PendingCatholicHouseholds";
import CatholicHouseholdStatistics from "./containers/CatholicHouseholdStatistics";
import {IoBarChart} from "react-icons/io5";
import {PiClockCountdown} from "react-icons/pi";
import EmptyHolder from "../../../components/EmptyHolder";
import {CatholicHouseholdMemberQueryBaptism} from "./containers/CatholicHouseholdMemberQueryBaptism";
import {CatholicHouseholdMemberQueryHolyOrders} from "./containers/CatholicHouseholdMemberQueryHolyOrders";
import {CatholicHouseholdMemberQueryFirstCommunion} from "./containers/CatholicHouseholdMemberQueryFirstCommunion";
import {CatholicHouseholdMemberQueryConfirmation} from "./containers/CatholicHouseholdMemberQueryConfirmation";
import {CatholicHouseholdMemberQueryMatrimony} from "./containers/CatholicHouseholdMemberQueryMatrimony";
import {TbListSearch} from "react-icons/tb";
import {BaptismSacramentSvg} from "../../../components/Svgs/BaptismSacramentSvg";
import {FirstCommunionSacramentSvg} from "../../../components/Svgs/FirstCommunionSacramentSvg";
import {ConfirmationSacramentSvg} from "../../../components/Svgs/ConfirmationSacramentSvg";
import {MatrimonySacramentSvg} from "../../../components/Svgs/MatrimonySacramentSvg";
import {HolyOrdersSacramentSvg} from "../../../components/Svgs/HolyOrdersSacramentSvg";
import useTranslate from "../../../modules/local/useTranslate";
import {CatholicHouseholdSvg} from "../../../components/Svgs/CatholicHouseholdSvg";


const ItemKeys = Object.freeze({
    CATHOLIC_HOUSEHOLD_STATS: 'catholic-household-stats',
    PENDING_CATHOLIC_HOUSEHOLDS: 'pending-catholic-households',
    CATHOLIC_HOUSEHOLDS: 'catholic-households',
    CATHOLIC_HOUSEHOLD_QUERY_GROUP: 'catholic-household-query-group',
    CATHOLIC_HOUSEHOLD_QUERY_BAPTISM: 'catholic-household-query-baptism',
    CATHOLIC_HOUSEHOLD_QUERY_FIRST_COMMUNION: 'catholic-household-query-first_communion',
    CATHOLIC_HOUSEHOLD_QUERY_CONFIRMATION: 'catholic-household-query-confirmation',
    CATHOLIC_HOUSEHOLD_QUERY_MATRIMONY: 'catholic-household-query-matrimony',
    CATHOLIC_HOUSEHOLD_QUERY_HOLY_ORDERS: 'catholic-household-query-holy_orders',
})


const WorkspaceCatholicHousehold = ({owner_id, owner_type}) => {

    const t = useTranslate()

    const menuItems = useMemo(() => {
        const menuItems = [
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLD_STATS,
                label: 'insights',
                icon: <IoBarChart size={16}/>,
            },
            {
                key: ItemKeys.PENDING_CATHOLIC_HOUSEHOLDS,
                label: 'pending catholic households',
                icon: <PiClockCountdown size={16}/>,
            },
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLDS,
                label: 'catholic household',
                icon: <CatholicHouseholdSvg size={16}/>,
            },
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_GROUP,
                label: 'lookup the sacraments',
                icon: <TbListSearch size={16}/>,
                children: [
                    {
                        key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_BAPTISM,
                        label: 'sacrament of baptism',
                        icon: <BaptismSacramentSvg size={22}/>,
                    },
                    {
                        key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_FIRST_COMMUNION,
                        label: 'first communion',
                        icon: <FirstCommunionSacramentSvg size={22}/>,
                    },
                    {
                        key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_CONFIRMATION,
                        label: 'sacrament of confirmation',
                        icon: <ConfirmationSacramentSvg size={22}/>,
                    },
                    {
                        key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_MATRIMONY,
                        label: 'sacrament of matrimony',
                        icon: <MatrimonySacramentSvg size={22}/>,
                    },
                    {
                        key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_HOLY_ORDERS,
                        label: 'sacrament of holy orders',
                        icon: <HolyOrdersSacramentSvg size={22}/>,
                    },
                ],
            },
        ]
        return menuItems;
    }, [])

    const routeEntities = useMemo(() => {
        const routeEntities = [
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLD_STATS,
                component: CatholicHouseholdStatistics,
            },
            {
                key: ItemKeys.PENDING_CATHOLIC_HOUSEHOLDS,
                component: PendingCatholicHouseholds,
            },
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLDS,
                component: CatholicHouseholds,
            },
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_BAPTISM,
                component: CatholicHouseholdMemberQueryBaptism,
            },
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_FIRST_COMMUNION,
                component: CatholicHouseholdMemberQueryFirstCommunion,
            },
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_CONFIRMATION,
                component: CatholicHouseholdMemberQueryConfirmation,
            },
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_MATRIMONY,
                component: CatholicHouseholdMemberQueryMatrimony,
            },
            {
                key: ItemKeys.CATHOLIC_HOUSEHOLD_QUERY_HOLY_ORDERS,
                component: CatholicHouseholdMemberQueryHolyOrders,
            },
        ].map(({key, ...rest}) => ({
            key,
            path: key,
            ...rest,
        }))
        return routeEntities;
    }, [])

    const {activeKey, Component, onMenuSelect} = useMenuHandler({
        routes: routeEntities,
        actionName: Queries.REL,
    })

    const renderMenu = useCallback(
        () => (
            <CustomSiderMenu
                deps={[activeKey]}
                Wrapper={renderOwnChild}>
                <SettingsMenu
                    items={menuItems}
                    onSelect={onMenuSelect}
                    defaultSelectedKeys={[activeKey]}
                />
            </CustomSiderMenu>
        ),
        [menuItems, activeKey]
    )


    return (
        <DefaultTemplate renderMenu={renderMenu}>
            <div className="space-y-3 p-2">
                <Component {...{owner_id, owner_type}} />
            </div>
        </DefaultTemplate>
    )
}

export default nest(
    withProps({lazy: true})(DelayRender),
    compose(
        fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
            owner_id: getId(organization),
            owner_type: getType(organization),
            organization
        })),
        branch(
            ({organization}) => Number(organization?.association_status) !== 1,
            () => () => <EmptyHolder
                title={"Content is not available"}
                subTitle={"The content will be available once the organization has completed linking with a parish"}
            />
        )
    )(WorkspaceCatholicHousehold)
)
